/**
 * Reconcile a REST/recovery detail-market snapshot with the currently visible
 * detail state. REST is a recovery/baseline snapshot; WebSocket patches are the
 * real-time transition source for price and lock/unlock changes.
 *
 * The important safety rule: never keep a missing REST row open. If a non-
 * terminal recovery snapshot omits a row that was visible a moment ago, render
 * it as a locked placeholder long enough for the display lifecycle to
 * flash/disable/evict it naturally. That covers the common race where REST
 * recovery lands before the matching WS lock patch.
 */
import { dedupMarkets, marketIdentityKey } from './sportsnewDetailPatch.js';
import { marketOdds } from './sportsnewDisplayLifecycle.js';

const TERMINAL_FIXTURE_STATES = new Set([
  'FINAL',
  'COMPLETED',
  'ENDED',
  'END',
  'FINISHED',
  'CANCELLED',
  'CANCELED',
  'ABANDONED',
  'SETTLED',
  'CLOSED',
]);

const TERMINAL_PERIODS = new Set([
  'END-REG',
  'END_REG',
  'END REG',
  'FULL_TIME',
  'FULL-TIME',
  'FT',
]);

const normalizeToken = (value) => String(value ?? '').trim().toUpperCase();

export const isTerminalFixtureSnapshot = (fixture) => {
  if (!fixture) return false;
  const status = normalizeToken(fixture.status ?? fixture.fixtureStatus ?? fixture.state ?? fixture.lifecycleStatus);
  const period = normalizeToken(fixture.period ?? fixture.periodStatus ?? fixture.matchPeriod);
  return TERMINAL_FIXTURE_STATES.has(status) || TERMINAL_PERIODS.has(period);
};

const marketKey = (market) => {
  const key = marketIdentityKey(market);
  if (key != null) return key;
  const id = market?.marketStateId ?? market?.id ?? market?.market_id;
  return id != null ? `state:${id}` : null;
};

const marketRenderId = (market) => market?.marketStateId ?? market?.id ?? market?.market_id ?? marketKey(market);

const itemArrayKey = (market, fallbackMarket) => {
  if (market?.selections) return 'selections';
  if (market?.odds) return 'odds';
  if (fallbackMarket?.selections) return 'selections';
  return 'odds';
};

const hasHiddenId = (setLike, id) => id != null && typeof setLike?.has === 'function' && setLike.has(id);

const oddId = (odd) => odd?.oddStateId ?? odd?.id ?? null;

const lockMissingOdd = (odd, lockedAtIso) => ({
  ...odd,
  priceDecimal: null,
  decimalOdds: null,
  price: null,
  odds: null,
  bettable: false,
  isBettable: false,
  is_suspended: true,
  suspended: true,
  locked: true,
  betting_locked: true,
  state: 'MARKET_UNAVAILABLE',
  displayState: 'locked',
  lockReason: odd?.lockReason ?? 'REST_SNAPSHOT_MISSING',
  lockedSince: odd?.lockedSince ?? lockedAtIso,
});

const shouldPreserveMissingOdd = (odd, hiddenOddIds) => {
  const id = oddId(odd);
  return id != null && !hasHiddenId(hiddenOddIds, id);
};

const lockedMissingOdds = (market, hiddenOddIds, lockedAtIso) => (
  marketOdds(market)
    .filter((odd) => shouldPreserveMissingOdd(odd, hiddenOddIds))
    .map((odd) => lockMissingOdd(odd, lockedAtIso))
);

const shouldPreserveMissingMarket = (market, hiddenMarketIds, hiddenOddIds) => {
  if (!market || hasHiddenId(hiddenMarketIds, marketRenderId(market))) return false;
  return marketOdds(market).some((odd) => shouldPreserveMissingOdd(odd, hiddenOddIds));
};

const mergeMissingOddsAsLocked = (incomingMarket, previousMarket, hiddenOddIds, lockedAtIso) => {
  if (!incomingMarket || !previousMarket) return incomingMarket;

  const key = itemArrayKey(incomingMarket, previousMarket);
  const incomingItems = incomingMarket[key] ?? [];
  const existingOddIds = new Set(incomingItems.map(oddId).filter((id) => id != null));
  const preserved = marketOdds(previousMarket).filter((odd) => {
    const id = oddId(odd);
    return id != null && !existingOddIds.has(id) && shouldPreserveMissingOdd(odd, hiddenOddIds);
  }).map((odd) => lockMissingOdd(odd, lockedAtIso));

  if (preserved.length === 0) return incomingMarket;
  return { ...incomingMarket, [key]: [...incomingItems, ...preserved] };
};

export const reconcileDetailMarketsFromRestSnapshot = (
  previousMarkets,
  restMarkets,
  { fixture = null, snapshotComplete = true, hiddenOddIds = new Set(), hiddenMarketIds = new Set(), now = Date.now() } = {}
) => {
  const incomingMarkets = dedupMarkets(Array.isArray(restMarkets) ? restMarkets : []);
  if (!Array.isArray(previousMarkets) || previousMarkets.length === 0) return incomingMarkets;
  if (isTerminalFixtureSnapshot(fixture)) return incomingMarkets;
  const lockedAtIso = new Date(now).toISOString();

  const previousByKey = new Map();
  previousMarkets.forEach((market) => {
    const key = marketKey(market);
    if (key != null && !previousByKey.has(key)) previousByKey.set(key, market);
  });

  const matchedPreviousKeys = new Set();
  const reconciled = incomingMarkets.map((market) => {
    const key = marketKey(market);
    const previousMarket = key != null ? previousByKey.get(key) : null;
    if (key != null && previousMarket) matchedPreviousKeys.add(key);
    return mergeMissingOddsAsLocked(market, previousMarket, hiddenOddIds, lockedAtIso);
  });

  previousMarkets.forEach((market) => {
    const key = marketKey(market);
    if (key != null && matchedPreviousKeys.has(key)) return;
    if (shouldPreserveMissingMarket(market, hiddenMarketIds, hiddenOddIds)) {
      // A bounded page says nothing about markets outside that page. Preserve
      // their current state; only a complete snapshot can declare them missing.
      if (!snapshotComplete) {
        reconciled.push(market);
        return;
      }
      const arrayKey = itemArrayKey(null, market);
      reconciled.push({ ...market, [arrayKey]: lockedMissingOdds(market, hiddenOddIds, lockedAtIso) });
    }
  });

  return dedupMarkets(reconciled);
};
